import React, { useCallback, useEffect, useState } from 'react';
import { API } from '@/App';

export const EMPTY_BRANCH_ACCESS = { profile_id: '', external_id: '', access_method: '', linked: false };
const METHODS = [['qr', 'QR'], ['manual', 'Numeric']];
const selectCls = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm';

// Tenant Access Profiles; null when the user can't manage them (field hidden).
export const useAccessProfiles = () => {
  const [profiles, setProfiles] = useState(null);
  const load = useCallback(async () => {
    const res = await fetch(`${API}/access-profiles`, { credentials: 'include' }).catch(() => null);
    setProfiles(res?.ok ? await res.json() : null);
  }, []);
  useEffect(() => { load(); }, [load]);
  return [profiles, load];
};

export const branchAccessFor = (profiles, branchId) => {
  for (const p of profiles || []) {
    const m = (p.branch_mapping || []).find(x => x.branch_id === branchId);
    if (m) return { profile_id: p.profile_id, external_id: m.external_id, access_method: m.access_method || '', linked: true };
  }
  return EMPTY_BRANCH_ACCESS;
};

const targetProfile = (profiles, value) =>
  profiles.find(p => p.profile_id === value.profile_id) || profiles.find(p => p.is_default) || profiles[0];

export const branchAccessError = (profiles, value, branchId) => {
  const code = (value.external_id || '').trim();
  if (!profiles?.length || !code) return '';
  if (!value.access_method) return 'Choose QR or Numeric for this branch\u2019s survey access.';
  const clash = (targetProfile(profiles, value).branch_mapping || [])
    .find(m => m.external_id.toUpperCase() === code.toUpperCase() && m.branch_id !== branchId);
  return clash ? `Store code ${code} is already used by another branch.` : '';
};

// Returns an error message, or null when saved / nothing to save.
export const saveBranchAccess = async (profiles, branchId, value) => {
  const code = (value.external_id || '').trim();
  if (!profiles?.length || (!code && !value.linked)) return null;
  const res = await fetch(`${API}/access-profiles/branch-access/${branchId}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ external_id: code, access_method: value.access_method, profile_id: targetProfile(profiles, value).profile_id }),
  });
  if (res.ok) return null;
  const data = await res.json().catch(() => ({}));
  return typeof data.detail === 'string' ? data.detail : 'Could not save survey access';
};

// Phase 4B — branch form: POS store code + QR/Numeric; rules come from the client's Access Profile.
export const BranchAccessFields = ({ profiles, value, onChange, branchId }) => {
  if (!profiles) return null;
  const heading = <h3 className="text-xs uppercase tracking-wider font-semibold text-[#1A3E6F]">Survey Access</h3>;
  if (!profiles.length) {
    return (
      <section className="space-y-2" data-testid="branch-access-section">
        {heading}
        <p className="text-[11px] text-[#64748B]" data-testid="branch-access-no-profile">
          To use receipt or QR codes, create an Access Profile in a survey&apos;s Settings first.
        </p>
      </section>
    );
  }
  const profile = targetProfile(profiles, value);
  const methods = METHODS.filter(([m]) => profile.methods?.[m]);
  const error = branchAccessError(profiles, value, branchId);
  const onlyMethod = (p) => { const ms = METHODS.filter(([m]) => p.methods?.[m]); return ms.length === 1 ? ms[0][0] : ''; };
  const setCode = (v) => onChange({ ...value, external_id: v, access_method: value.access_method || onlyMethod(profile) });
  const setProfile = (id) => {
    const next = profiles.find(p => p.profile_id === id);
    const keep = value.access_method && next.methods?.[value.access_method];
    onChange({ ...value, profile_id: id, access_method: keep ? value.access_method : onlyMethod(next) });
  };
  return (
    <section className="space-y-3" data-testid="branch-access-section">
      {heading}
      <p className="text-[11px] text-[#64748B]" data-testid="branch-access-inherit-note">
        Validation rules come from the &ldquo;{profile.name}&rdquo; Access Profile &mdash; nothing else to set up.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {profiles.length > 1 && (
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="branch_access_profile">Access Profile</label>
            <select id="branch_access_profile" value={profile.profile_id} className={selectCls} data-testid="branch-access-profile-select"
              onChange={(e) => setProfile(e.target.value)}>
              {profiles.map(p => <option key={p.profile_id} value={p.profile_id}>{p.name}{p.is_default ? ' (default)' : ''}</option>)}
            </select>
          </div>
        )}
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="branch_pos_code">External / POS Store Code</label>
          <input id="branch_pos_code" value={value.external_id} onChange={(e) => setCode(e.target.value)} maxLength={40}
            placeholder="e.g. 360" className={selectCls} data-testid="branch-pos-code-input" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="branch_access_method">Access Method</label>
          <select id="branch_access_method" value={value.access_method} className={selectCls} data-testid="branch-access-method-select"
            onChange={(e) => onChange({ ...value, access_method: e.target.value })}>
            <option value="">Select…</option>
            {methods.map(([m, label]) => <option key={m} value={m}>{label}</option>)}
          </select>
        </div>
      </div>
      {error && <p className="text-xs text-[#D62B27]" data-testid="branch-access-error">{error}</p>}
    </section>
  );
};
